import { Component, ViewChild, ElementRef, AfterViewInit, OnInit, ChangeDetectorRef } from "@angular/core";
import { MatBottomSheetRef, MAT_BOTTOM_SHEET_DATA } from "@angular/material";
import { Inject } from "@angular/core";
import { ApiService } from "../../../../services/api.service";
import { AlertService, MessageSeverity } from "../../../../services/alert.service";

declare var Quill: any;

/**
 * Editor for a benefit's Learn More copy. The copy is stored once per feature and shared
 * by every whitelabel that enables the benefit, so edits here affect all sites.
 *
 * Two modes, because the copy arrives in two shapes. The content team supplies complete
 * HTML documents -- doctype, <head>, their own <style> -- which a rich-text editor would
 * destroy, so those are edited as source. Simple fragments can be edited in Quill.
 * isFullDocument() decides which mode to open in and blocks the destructive switch.
 */
@Component({
    selector: 'learn-more-content-sheet',
    template: require('./learn-more-content-sheet.html'),
    styles: [require('./learn-more-content-sheet.css')],
})
export class LearnMoreContentSheetComponent implements OnInit, AfterViewInit {

    @ViewChild('quillHost') quillHost: ElementRef;
    @ViewChild('previewFrame') previewFrame: ElementRef;

    whitelabelFeature: any;
    html = '';
    mode: 'source' | 'rich' = 'source';
    loading = true;
    saving = false;

    private quill: any = null;

    constructor(private changeRef: ChangeDetectorRef,
                private alertService: AlertService,
                private apiService: ApiService,
                @Inject(MAT_BOTTOM_SHEET_DATA) public data: any,
                private bottomSheetRef: MatBottomSheetRef<LearnMoreContentSheetComponent>) {
        this.whitelabelFeature = data.whitelabelFeature;
    }

    ngOnInit() {
        this.apiService.getLearnMoreContent(this.whitelabelFeature.whitelabelFeatureId).subscribe(r => {
            this.html = (r && r.learnMoreHtml) || '';
            this.mode = this.isFullDocument() ? 'source' : 'rich';
            this.loading = false;
            this.changeRef.detectChanges();
            this.syncEditors();
        }, () => {
            this.loading = false;
            this.changeRef.detectChanges();
        });
    }

    ngAfterViewInit() {
        this.syncEditors();
    }

    /** A whole page rather than a fragment; Quill would strip everything outside <body>. */
    isFullDocument(): boolean {
        return /<!doctype|<html[\s>]|<head[\s>]|<body[\s>]/i.test(this.html || '');
    }

    setMode(mode: 'source' | 'rich') {
        if (mode === 'rich' && this.isFullDocument()) {
            this.alertService.showMessage("",
                "This benefit's copy is a complete HTML page. Editing it as rich text would discard its <head> and styling, so it stays in source mode.",
                MessageSeverity.warn);
            return;
        }
        this.mode = mode;
        this.changeRef.detectChanges();
        this.syncEditors();
    }

    onSourceChanged() {
        this.refreshPreview();
    }

    private syncEditors() {
        if (this.mode === 'rich') {
            this.initQuill();
            if (this.quill) {
                this.quill.root.innerHTML = this.html || '';
            }
        }
        this.refreshPreview();
    }

    private initQuill() {
        if (this.quill || !this.quillHost || typeof Quill === 'undefined') { return; }

        this.quill = new Quill(this.quillHost.nativeElement, {
            theme: 'snow',
            modules: {
                toolbar: [
                    [{ header: [1, 2, 3, false] }],
                    ['bold', 'italic', 'underline'],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    ['link', 'blockquote'],
                    ['clean']
                ]
            }
        });

        this.quill.on('text-change', () => {
            this.html = this.quill.root.innerHTML;
            this.refreshPreview();
        });
    }

    /** Same sandboxed iframe the member portal uses, so the preview is faithful. */
    private refreshPreview() {
        if (!this.previewFrame) { return; }
        this.previewFrame.nativeElement.srcdoc = this.html || '<p style="font:14px sans-serif;color:#888">Nothing to preview yet.</p>';
    }

    onFileSelected(event: any) {
        const file = event.target && event.target.files && event.target.files[0];
        if (!file) { return; }

        const reader = new FileReader();
        reader.onload = () => {
            this.html = String(reader.result || '');
            this.mode = this.isFullDocument() ? 'source' : this.mode;
            this.changeRef.detectChanges();
            this.syncEditors();
        };
        reader.readAsText(file);
    }

    save() {
        this.saving = true;
        this.apiService.saveLearnMoreContent(this.whitelabelFeature.whitelabelFeatureId, this.html).subscribe(() => {
            this.alertService.showMessage("", "Learn More content saved for " + this.whitelabelFeature.featureName + ".", MessageSeverity.success);
            this.saving = false;
            this.bottomSheetRef.dismiss(true);
        }, () => {
            this.saving = false;
            this.changeRef.detectChanges();
        });
    }

    removeContent() {
        this.apiService.deleteLearnMoreContent(this.whitelabelFeature.whitelabelFeatureId).subscribe(() => {
            this.alertService.showMessage("", "Learn More content removed.", MessageSeverity.success);
            this.bottomSheetRef.dismiss(true);
        });
    }

    cancel() {
        this.bottomSheetRef.dismiss();
    }
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./ClientApp/app/components/whitelabels/whitelabel-configs/learn-more-content/learn-more-content-sheet.ts